import { useAuthStore } from '@/entities/auth';
import { apolloOperationGate } from '@/shared/api';
import { rememberOrganizationLocation, useLocationStorage, useOrganizationStorage } from '@/shared/config';

export type LocationSwitchFailureReason =
  'context-failure' | 'unauthenticated' | 'organization-mismatch' | 'location-unavailable';

export type LocationSwitchResult =
  | { status: 'ready'; organizationId: string; locationId: string | null }
  | { status: 'failure'; reason: LocationSwitchFailureReason };

/**
 * Composable для переключения между локациями
 */
export function useLocationSwitch() {
  const authStore = useAuthStore();
  const { getOrganizationId, setOrganizationId, resetOrganizationId } = useOrganizationStorage();
  const { getLocationId, setLocationId, clearLocationId } = useLocationStorage();

  /**
   * Переключает текущую локацию (и организацию при необходимости)
   * @param organizationId - ID организации для переключения
   * @param locationId - ID локации для переключения (опционально)
   */
  const switchLocation = (organizationId: string, locationId?: string): Promise<LocationSwitchResult> => {
    return apolloOperationGate.runExclusive('tenant-transition', async () => {
      // Целевую локацию ставим сразу, если она уже в доступных: иначе на время
      // загрузки контекста currentLocation === null и sidebar на секунду прячет
      // пункты с requiresLocation («Запись», «Витрина»), а шапка мигает
      // «Локация не выбрана». Проверки ниже откатят её, если контекст не сойдётся.
      const optimisticLocation = locationId
        ? authStore.availableLocations.find((loc) => loc.id === locationId)
        : undefined;

      if (optimisticLocation) {
        authStore.setLocation(optimisticLocation);
      } else {
        authStore.clearLocation();
      }

      // Сохраняем organization ID в storage
      setOrganizationId(organizationId);

      // Сохраняем location ID если передан
      if (locationId) {
        setLocationId(locationId);
      } else {
        clearLocationId();
      }

      // Загружаем контекст с новой организацией
      const contextResult = await authStore.loadContext();

      if (contextResult.status === 'failure') {
        resetOrganizationId();
        clearLocationId();
        authStore.clearLocation();
        return { status: 'failure', reason: 'context-failure' };
      }

      if (contextResult.status === 'unauthenticated') {
        resetOrganizationId();
        clearLocationId();
        return { status: 'failure', reason: 'unauthenticated' };
      }

      if (authStore.currentOrganization?.id !== organizationId) {
        resetOrganizationId();
        clearLocationId();
        authStore.clearLocation();
        return { status: 'failure', reason: 'organization-mismatch' };
      }

      // Устанавливаем текущую локацию в store после загрузки контекста
      if (locationId) {
        const location = authStore.availableLocations.find((loc) => loc.id === locationId);

        if (!location) {
          resetOrganizationId();
          clearLocationId();
          authStore.clearLocation();
          return { status: 'failure', reason: 'location-unavailable' };
        }

        authStore.setLocation(location);
        rememberOrganizationLocation(organizationId, locationId);
      }

      return {
        status: 'ready',
        organizationId,
        locationId: locationId ?? null,
      };
    });
  };

  /**
   * Сбрасывает выбранную организацию и локацию
   */
  const resetLocation = () => {
    resetOrganizationId();
    clearLocationId();
    authStore.clearContext();
  };

  return {
    getOrganizationId,
    setOrganizationId,
    getLocationId,
    setLocationId,
    switchLocation,
    resetLocation,
  };
}
